import {RunJobActions,RunContactActions,RunHistory,runStatusLabel,runStatusClass} from "@/components/RunOrderCard";
import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Truck, CheckCircle, XCircle, Play, MapPin } from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}
function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }
function fmtDate(s: string | null) { return s ? new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"; }

interface Run {
  id: number; storeId: number; storeName: string | null; status: string;
  driverId: number | null; driverName: string | null; dropCount: number;
  pickupAddress: string | null; dropFeesTotalPence: number; fuelSupplementPence: number;
  hubFeePence: number; scheduledFor: string | null; claimedAt: string | null;
  completedAt: string | null; createdAt: string;
}
interface DriverOpt {completedCount?:number;isHighValueTrusted?:boolean; userId: number; name: string; vehicleType: string | null; isOnline: boolean }

const STATUS_STYLE: Record<string, string> = {
  dispatching: "bg-blue-100 text-blue-700 border-blue-200",
  assigned: "bg-amber-100 text-amber-700 border-amber-200",
  in_progress: "bg-indigo-100 text-indigo-700 border-indigo-200",
  completed: "bg-green-100 text-green-700 border-green-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};
const ACTIVE = new Set(["dispatching", "assigned", "in_progress"]);

export default function AdminDeliveryRuns(){return <DeliveryRunsControls />;}

export function DeliveryRunsControls({embedded=false}:{embedded?:boolean}={}) {
  const { user, isLoading } = useAuth();
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError,setLoadError]=useState("");

  async function load() {
    setLoading(true);
    try {
    const response=await fetch("/api/external-runs/admin/list",{headers:authHeaders()});
    const data=await response.json();if(!response.ok)throw Error(data.error??"Could not refresh jobs. Please retry.");
    setRuns(data.runs??[]);
    setLoadError("");
    } catch(e){setLoadError((e as Error).message);} finally{setLoading(false);}
  }
  useEffect(() => { void load(); const timer=setInterval(()=>void load(),8000);return ()=>clearInterval(timer); }, []);

  if (isLoading) return null;
  if (!user || user.role !== "admin") return <Redirect to="/admin" />;

  const active = runs.filter(r => ACTIVE.has(r.status));
  const past = runs.filter(r => !ACTIVE.has(r.status));

  function renderRow(r: Run) {
    return (
      <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-foreground">Job #{r.id}</span>
              <Badge className={`text-xs border ${runStatusClass(r.status)}`}>{runStatusLabel(r.status)}</Badge>
              <span className="text-sm text-slate-500">{r.storeName ?? `Store ${r.storeId}`}</span>
              <span className="text-xs text-slate-400">· {r.dropCount} drop{r.dropCount === 1 ? "" : "s"}</span>
            </div>
            {r.pickupAddress && <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin size={11} /> {r.pickupAddress}</p>}
            <p className="text-xs text-slate-400 mt-0.5">
              Driver: <span className="text-slate-600 font-medium">{r.driverName ?? "— unassigned —"}</span>
              {" · "}Created {fmtDate(r.createdAt)}{r.completedAt ? ` · Completed ${fmtDate(r.completedAt)}` : ""}
            </p>
            <p className="text-xs text-slate-400">Driver pay {pence(r.dropFeesTotalPence + r.fuelSupplementPence)} · Hub fee {pence(r.hubFeePence)}</p>
          </div>
        </div>
        {r.driverId&&<RunContactActions run={r} headers={authHeaders()}/>}
        <RunHistory runId={r.id} headers={authHeaders()} admin/>
        <RunJobActions run={r} headers={authHeaders()} admin onChanged={load}/>

      </div>
    );
  }

  const content = (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2"><Truck size={22} className="text-indigo-500" /> Delivery Runs</h1>
          <p className="text-sm text-slate-500 mt-1">Assign or reassign a driver, force-complete, or cancel any multidrop run.</p>
        </div>
        {loadError&&<p role="alert" className="text-sm text-red-700">{loadError}<button className="ml-2 underline" onClick={()=>void load()}>Retry</button></p>}
        {loading && runs.length===0 ? <p className="text-slate-500">Loading…</p> : (
          <>
            <section>
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Active ({active.length})</h2>
              {active.length === 0 ? <p className="text-sm text-slate-400">No active runs.</p> : <div className="space-y-2">{active.map(renderRow)}</div>}
            </section>
            {!embedded && past.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">History ({past.length})</h2>
                <div className="space-y-2">{past.map(renderRow)}</div>
              </section>
            )}
          </>
        )}
      </div>
  );
  return embedded?content:<AdminLayout current="/admin/delivery-runs">{content}</AdminLayout>;
}
